import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import WarningRounded from "@material-ui/icons/WarningRounded";
import ArrowForward from "@material-ui/icons/ArrowForward";
import Close from "@material-ui/icons/Close";
import moment from "moment";
import axios from "utils/httpClient";

const useStyles = makeStyles(theme => ({
  paper: { borderRadius: 16, margin: 16, width: "calc(100% - 32px)" },
  title: { padding: "20px 24px", display: "flex", alignItems: "center", gap: 14 },
  icon: { display: "flex", padding: 12, borderRadius: 14, color: "#b45309", background: "#fff7ed" },
  titleText: { flex: 1, minWidth: 0 },
  content: { background: "#f8fafc", padding: "20px 24px", borderTop: "1px solid #e2e8f0", borderBottom: "1px solid #e2e8f0" },
  notice: { padding: 16, borderRadius: 12, background: "#fff7ed", border: "1px solid #fed7aa", color: "#7c2d12", marginBottom: 20 },
  summary: { display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 12 },
  count: { background: "#e0e7ff", color: "#3730a3", fontWeight: 700 },
  list: { margin: 0, padding: 0, listStyle: "none" },
  request: { display: "flex", gap: 12, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 16, marginBottom: 12 },
  number: { flexShrink: 0, width: 28, height: 28, borderRadius: "50%", background: "#eef2ff", color: "#4338ca", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700 },
  requestBody: { flex: 1, minWidth: 0 },
  route: { display: "grid", gridTemplateColumns: "minmax(0, 1fr) 24px minmax(0, 1fr)", gap: 12, alignItems: "center", overflowWrap: "anywhere" },
  times: { marginTop: 12, padding: "10px 12px", background: "#f8fafc", borderRadius: 8 },
  legs: { display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 },
  leg: { fontSize: 12, color: "#475569", border: "1px solid #e2e8f0", borderRadius: 6, padding: "4px 8px", overflowWrap: "anywhere" },
  error: { padding: 12, borderRadius: 8, background: "#fef2f2", color: "#b91c1c", marginBottom: 12 },
  loading: { display: "flex", alignItems: "center", justifyContent: "center", gap: 12, padding: "40px 0" },
  actions: { padding: "16px 24px", gap: 8, [theme.breakpoints.down("xs")]: { flexWrap: "wrap", "& > button": { flexGrow: 1 } } },
  confirm: { background: "#b91c1c", color: "#fff", boxShadow: "none", padding: "10px 18px", borderRadius: 8, "&:hover": { background: "#991b1b", boxShadow: "none" } }
}));

const format = (value, pattern = "DD/MM/BBBB HH:mm") => value && moment(value).isValid() ? moment(value).format(pattern) : "-";

export default function VehicleAssignmentCancellationDialog({ target, onClose, onCancelled, onBusyChange }) {
  const classes = useStyles();
  const assignmentId = target?.assignmentID;
  const [state, setState] = useState({ id: null, data: null, loading: false, error: "" });
  const [revision, setRevision] = useState(0);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [saveError, setSaveError] = useState("");
  const preview = state.id === assignmentId ? state.data : null;
  const loading = !!assignmentId && (state.id !== assignmentId || state.loading);

  useEffect(() => { setSaveError(""); }, [assignmentId]);
  useEffect(() => {
    if (!assignmentId) return;
    let current = true;
    setState({ id: assignmentId, data: null, loading: true, error: "" });
    axios.get(`/api/v1/vehiclebooking/assignments/${assignmentId}/cancellation-preview`).then(response => {
      if (!current) return;
      if (response.data.result !== 200) throw new Error(response.data.msg || "ไม่สามารถโหลดรายการขอใช้รถได้");
      const data = response.data.data;
      if (data?.assignmentID !== assignmentId || !data.version || !Array.isArray(data.bookings) || data.bookingCount !== data.bookings.length
        || data.bookings.some(booking => !booking?.vbid || !Array.isArray(booking.legs)))
        throw new Error("ข้อมูลรายการขอใช้รถไม่ครบถ้วน กรุณาโหลดรายการอีกครั้ง");
      setState({ id: assignmentId, data, loading: false, error: "" });
    }).catch(error => {
      if (current) setState({ id: assignmentId, data: null, loading: false, error: error.response?.data?.msg || error.message || "ไม่สามารถโหลดรายการขอใช้รถได้" });
    });
    return () => { current = false; };
  }, [assignmentId, revision]);

  const confirm = async () => {
    if (savingRef.current || loading || !preview?.version || !preview.bookingCount) return;
    savingRef.current = true; setSaving(true); onBusyChange(true); setSaveError("");
    try {
      const response = await axios.post(`/api/v1/vehiclebooking/assignments/${assignmentId}/cancel`, { version: preview.version });
      if (response.data.result !== 200) throw new Error(response.data.msg || "ไม่สามารถยกเลิกการจัดรถได้");
      await onCancelled(response.data.msg || "ยกเลิกการจัดรถเรียบร้อยแล้ว");
    } catch (error) {
      if (error.response?.status !== 401) setSaveError(error.response?.data?.msg || error.message || "ไม่สามารถยกเลิกการจัดรถได้ กรุณาลองใหม่");
      if (error.response?.status === 409) setRevision(value => value + 1);
    } finally { savingRef.current = false; setSaving(false); onBusyChange(false); }
  };
  const close = () => { if (!savingRef.current) onClose(); };

  return (
    <Dialog open={!!target} fullWidth maxWidth="md" PaperProps={{ className: classes.paper }} onClose={close}
      aria-labelledby="cancel-assignment-title" aria-describedby="cancel-assignment-description">
      <DialogTitle disableTypography className={classes.title}>
        <div className={classes.icon}><WarningRounded /></div>
        <div className={classes.titleText}>
          <Typography variant="h6" id="cancel-assignment-title" style={{ fontWeight: 700 }}>ยืนยันยกเลิกการจัดรถ</Typography>
          <Typography variant="body2" color="textSecondary">ตรวจสอบรายการขอใช้รถที่ได้รับผลกระทบก่อนยืนยัน</Typography>
        </div>
        <IconButton aria-label="ปิดหน้าต่างยืนยัน" disabled={saving} onClick={close}><Close /></IconButton>
      </DialogTitle>
      <DialogContent className={classes.content}>
        {loading && <div className={classes.loading}><CircularProgress size={24} /><Typography>กำลังโหลดรายการขอใช้รถร่วมเที่ยว...</Typography></div>}
        {(state.id === assignmentId && state.error) && <div role="alert" className={classes.error}>{state.error}</div>}
        {saveError && <div role="alert" className={classes.error}>{saveError}</div>}
        {!loading && !preview && <Button onClick={() => setRevision(value => value + 1)}>โหลดรายการอีกครั้ง</Button>}
        {preview && <>
          <div className={classes.notice} id="cancel-assignment-description">
            <Typography style={{ fontWeight: 700, marginBottom: 4 }}>การยกเลิกครั้งนี้มีผลกับคำขอทั้ง {preview.bookingCount} รายการ</Typography>
            <Typography variant="body2">คำขอที่แสดงด้านล่างอยู่ในชุดจัดรถเดียวกัน เมื่อยืนยัน ระบบจะยกเลิกการจัดรถของทุกรายการพร้อมกัน รวมทุกเที่ยวขาไปและขากลับในชุดนี้</Typography>
          </div>
          <div className={classes.summary}>
            <Typography style={{ fontWeight: 700 }}>รายการขอใช้รถร่วมเที่ยว</Typography>
            <Chip size="small" className={classes.count} label={`${preview.bookingCount} คำขอ`} />
            <Typography variant="body2" color="textSecondary">ครอบคลุม {preview.turnCount} เที่ยวรถที่จัดไว้</Typography>
          </div>
          <ol className={classes.list} aria-label="รายการขอใช้รถที่จะยกเลิกการจัดรถ">
            {preview.bookings.map((booking, index) => {
              const oneWay = Number(booking.isOneWay) === 1;
              const sameDay = oneWay || (booking.startDatetime && booking.endDatetime && moment(booking.startDatetime).isSame(moment(booking.endDatetime), "day"));
              return <li key={booking.vbid} className={classes.request}>
                <span className={classes.number}>{index + 1}</span>
                <div className={classes.requestBody}>
                  <div className={classes.route}>
                    <div><Typography variant="caption" color="textSecondary">ต้นทาง</Typography><Typography style={{ fontWeight: 700 }}>{booking.startPlace || "ไม่ระบุต้นทาง"}</Typography></div>
                    <ArrowForward fontSize="small" style={{ color: "#94a3b8" }} />
                    <div><Typography variant="caption" color="textSecondary">ปลายทาง</Typography><Typography style={{ fontWeight: 700 }}>{booking.endPlace || "ไม่ระบุปลายทาง"}</Typography></div>
                  </div>
                  {booking.vbObjective && <Typography variant="body2" color="textSecondary" style={{ marginTop: 6 }}>{booking.vbObjective}</Typography>}
                  {oneWay && <Chip size="small" label="เที่ยวเดียว" style={{ marginTop: 6 }} />}
                  <div className={classes.times}>
                    <Typography variant="caption" color="textSecondary">วันเวลาที่ขอใช้{sameDay ? ` • ${format(booking.startDatetime, "DD/MM/BBBB")}` : ""}</Typography>
                    <Typography variant="body2">ขาไป: {format(booking.startDatetime, sameDay ? "HH:mm" : "DD/MM/BBBB HH:mm")}{!oneWay && ` · ขากลับ: ${format(booking.endDatetime, sameDay ? "HH:mm" : "DD/MM/BBBB HH:mm")}`}</Typography>
                  </div>
                  <div className={classes.legs}>{booking.legs.map(leg => (
                    <span key={`${leg.assignmentTurnID}-${leg.goBackKey}`} className={classes.leg}>
                      จัด{leg.goBackKey === 1 ? "ขาไป" : "ขากลับ"}: {format(leg.goBackKey === 1 ? leg.startDatetime : leg.endDatetime)}{leg.vehicleName ? ` • ${leg.vehicleName}` : ""}
                    </span>
                  ))}</div>
                </div>
              </li>;
            })}
          </ol>
        </>}
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button disabled={saving} onClick={close}>ปิด</Button>
        <Button variant="contained" className={classes.confirm} disabled={saving || loading || !preview?.bookingCount} onClick={confirm}>
          {saving ? "กำลังยกเลิก..." : `ยืนยันยกเลิกทั้งหมด${preview ? ` (${preview.bookingCount} คำขอ)` : ""}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

VehicleAssignmentCancellationDialog.propTypes = {
  target: PropTypes.shape({ assignmentID: PropTypes.string.isRequired }),
  onClose: PropTypes.func.isRequired,
  onCancelled: PropTypes.func.isRequired,
  onBusyChange: PropTypes.func.isRequired
};
